import { DirectedGraph } from "@p0security/shared/graph/types";
import {
  ALL_SCOPE_SENTINEL,
  AssessmentScopeIntegration,
  ProviderOrAll,
  scopeToProvider,
  toIntegration,
  toKey,
  toScope,
} from "@p0security/shared/types/assessment";
import { AssessmentNodes } from "@p0security/shared/types/assessment/data/index";
import { SelectedEnvironmentContext } from "components/Environment/contexts/SelectedEnvironmentContext";
import { ErrorDisplay } from "components/Error";
import { useAuthFetch } from "components/Login/hook";
import { useGuardedEffect } from "hooks/useGuardedEffect";
import { useObjectMemo } from "hooks/useObjectMemo";
import { useFlags } from "launchdarkly-react-client-sdk";
import { isArray, noop } from "lodash";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { StringParam, UrlUpdateType, useQueryParam } from "use-query-params";

import { GraphProcessingStep } from "../components/GraphStep";
import { CachedMetagraph, fetchGraphForScope } from "../utils/graph";

type ScopeContext = {
  graph: DirectedGraph<AssessmentNodes> | undefined;
  integration: AssessmentScopeIntegration | "all";
  // TODO: Remove AWS hardcoding
  integrationMeta?: { idc?: { id: string } };
  percentage: number;
  provider: ProviderOrAll;
  /** Clears and reloads graph */
  reset: () => void;
  scopeKey: string;
  setScopeKey: (scope: string, updateType?: UrlUpdateType) => void;
  step: GraphProcessingStep;
  validScopeKeys: Set<string>;
};

export const ScopeContext = createContext<ScopeContext>({
  graph: undefined,
  integration: ALL_SCOPE_SENTINEL,
  percentage: 0,
  provider: ALL_SCOPE_SENTINEL,
  reset: noop,
  scopeKey: ALL_SCOPE_SENTINEL,
  setScopeKey: noop,
  step: "loading" as const,
  validScopeKeys: new Set(ALL_SCOPE_SENTINEL),
});

export const ScopeProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { assessment, details, last } = useContext(SelectedEnvironmentContext);
  const assessmentId = details?.assessmentId;
  const flags = useFlags();
  const [error, setError] = useState<string>();
  const authFetch = useAuthFetch(setError);
  const [graph, setGraph] = useState<DirectedGraph<AssessmentNodes>>();
  // Cache metagraph to save large downloads / data processing.
  // Only the metagraph as we could consume too much memory with single-target
  // caches.
  const [metaGraph, setMetaGraph] = useState<CachedMetagraph>();
  const [step, setStep] = useState<GraphProcessingStep>("requested");
  const [percentage, setPercentage] = useState<number>(0);
  const [integrationMeta, setIntegrationMeta] = useState<{
    idc?: { id: string };
  }>();
  const [scopeKey, setScopeKey] = useQueryParam("scope", StringParam);

  const effectiveScopeKey = useMemo(
    () => scopeKey || ALL_SCOPE_SENTINEL,
    [scopeKey]
  );

  const onError = useCallback((error: any) => {
    console.error(error);
    setError(error);
  }, []);

  const validScopeKeys = useMemo(() => {
    const valid = new Set<string>([ALL_SCOPE_SENTINEL]);
    if (!isArray(last.doc?.data.scope)) return valid;
    for (const scope of last.doc?.data.scope ?? []) {
      valid.add(toKey(scope));
    }
    return valid;
  }, [last.doc?.data.scope]);

  // TODO: Move this somewhere aws-specific
  useGuardedEffect(
    (cancellation) => async () => {
      const effectiveScope = toScope(effectiveScopeKey);
      if (effectiveScope === ALL_SCOPE_SENTINEL) return; // TODO: remove eventually when we can derive url for each individual account
      if (effectiveScope.integration !== "aws") return;
      if (!flags.assessmentAwsRoleDeeplink) return;
      setIntegrationMeta(undefined);
      const response = await authFetch(
        `assessment/scope/${effectiveScopeKey}/integration-meta`,
        { method: "GET" }
      );
      if (!response) return;

      const data = (await response.json()) as {
        additionalContext: { idc?: { id: string } };
      };
      if (cancellation.isCancelled) return;
      setIntegrationMeta(data.additionalContext);
    },
    [
      assessmentId,
      authFetch,
      effectiveScopeKey,
      flags.assessmentAwsRoleDeeplink,
      last.doc,
      scopeKey,
    ],
    onError
  );

  // Download and boost graph when:
  // - A new environment is selected
  // - An assessment job completes
  // - The selected scope changes
  //
  // Caches the metagraph so that reselecting "All targets" is quick.
  useGuardedEffect(
    (cancellation) => async () => {
      if (!assessmentId || !last.doc) return;
      if (last.doc.data.assessmentId !== assessmentId) {
        setGraph(undefined);
        setStep("requested");
        return;
      }
      // Assessments without a graph (e.g. copilot-studio) have no GCS graph file
      // to stream — skip the fetch entirely to avoid a 404.
      if (assessment.doc?.data.hasGraph === false) return;

      setGraph(undefined);

      // If scope is all and there's an up-to-date cached metagraph, use that
      if (
        metaGraph?.assessmentId === assessmentId &&
        metaGraph?.jobId === last.doc?.id &&
        effectiveScopeKey === ALL_SCOPE_SENTINEL
      ) {
        setGraph(metaGraph.graph);
        setStep("aggregating");
        return;
      }

      cancellation.onCancel = () => {
        setGraph(graph);
        setStep(step);
      };

      fetchGraphForScope({
        assessmentId,
        authFetch,
        cancellation,
        effectiveScopeKey,
        lastJobId: last.doc.id,
        setGraph,
        setMetaGraph,
        setPercentage,
        setStep,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- previous values ('graph', 'lastGraphScopeKey', and 'step') in dependencies lead to infinite render loop. We set these again inside the hook.
    [
      assessment.doc,
      assessmentId,
      authFetch,
      effectiveScopeKey,
      last.doc,
      metaGraph?.assessmentId,
      metaGraph?.jobId,
    ],
    onError
  );

  const scope = toScope(effectiveScopeKey);
  const integration = toIntegration(scope);

  const reset = useCallback(() => setGraph(undefined), []);

  const value = useObjectMemo<ScopeContext>({
    graph,
    integration,
    integrationMeta,
    percentage,
    provider: scopeToProvider(integration),
    reset,
    scopeKey: effectiveScopeKey,
    setScopeKey,
    step,
    validScopeKeys,
  });

  return (
    <ScopeContext.Provider value={value}>
      {error && (
        <ErrorDisplay title="Error loading environment" error={String(error)} />
      )}
      {children}
    </ScopeContext.Provider>
  );
};
